import {
  type Action,
  type CryptoCurrency,
  type CryptoCurrencyDataSource,
  type FiatCurrency,
  setActionPrices,
} from '@0xtorch/core'
import {
  type BlockfrostClient,
  BlockfrostError,
  analyzeCardanoTransaction,
  cardanoAda,
  cardanoAssetMetadataSchema,
  cardanoEpochSchema,
  cardanoPaymentAddressSchema,
  cardanoQuantitySchema,
  cardanoRegistrationSchema,
  cardanoRewardSchema,
  cardanoStakeCertificateSchema,
  cardanoTransactionIndexSchema,
  cardanoTransactionSchema,
  cardanoUtxosSchema,
  cardanoWithdrawalSchema,
  createCardanoNativeAsset,
  isOwnedCardanoPaymentAddress,
} from '@pkg/cardano'
import {
  type AccountCardano,
  type ActionSourceCardano,
  type CardanoEvent,
  getCardanoSyncState,
  getSyncTimestamp,
  saveCardanoActions,
  saveCardanoSyncPage,
  setCardanoIndexComplete,
  upsertSyncTimestamp,
} from '@pkg/database-portfolio'
import { z } from 'zod'
import type { RunToDatabase } from './types'

/** 同じ browser からの重複同期を防ぐ。 */
let analyzing = false

/**
 * Cardano の所有アドレス探索、Tx/報酬索引、解析、価格設定、成功分保存を実行する。
 * @param params DB操作、全登録済みAccount、同期対象、認証済みAPI clientと既存価格基盤
 * @returns 未完了はDBへ保持し、成功分を保存した時点で解決するPromise
 */
export const analyzeCardanoAccounts = async (params: {
  readFromDatabase: RunToDatabase
  writeToDatabase: RunToDatabase
  accounts: readonly AccountCardano[]
  targetAccountIds: ReadonlySet<number>
  client: BlockfrostClient
  startTime: number
  cryptoCurrencies: readonly CryptoCurrency[]
  fiat: FiatCurrency
  priceDataSource: CryptoCurrencyDataSource
  onProgress: (
    params:
      | { status: 'check-account' | 'analyze'; progress: number }
      | undefined,
  ) => void
  onError: (error: unknown) => void
}): Promise<void> => {
  if (analyzing) return
  analyzing = true
  const { client, readFromDatabase, writeToDatabase } = params
  const targets = params.accounts.filter((account) =>
    params.targetAccountIds.has(account.id),
  )
  const assets = new Map<string, { asset: CryptoCurrency; decimals: number }>()
  const registrations = new Map<
    string,
    z.infer<typeof cardanoRegistrationSchema>[]
  >()
  const epochs = new Map<number, number>()
  const stakeAddresses = new Set(
    params.accounts.flatMap((account) =>
      account.cardanoStakeAddress === null ? [] : [account.cardanoStakeAddress],
    ),
  )
  /** アドレス単位で所有判定する登録済みEnterprise Address。 */
  const enterpriseAddresses = new Set(
    params.accounts
      .filter((account) => account.cardanoStakeAddress === null)
      .map((account) => account.cardanoAddress),
  )
  let halted = false
  let retryAt = 0
  /** API制限時は同じ同期内で別endpointへリクエストを連打しない。 */
  const report = ({ error }: { error: unknown }) => {
    if (
      error instanceof BlockfrostError &&
      [402, 418, 429].includes(error.status)
    ) {
      halted = true
      retryAt =
        error.status === 402 || error.status === 418
          ? Math.floor(Date.now() / 86_400_000) * 86_400_000 + 86_401_000
          : Math.max(Date.now() + 60_000, error.retryAt ?? 0)
    }
    params.onError(error)
  }
  /**
   * 配分Epochを実際のAPI時刻へ解決し、番号も照合する。
   * @param value 配分Epoch
   * @returns 開始時刻（ミリ秒）
   */
  const epochTime = async ({
    epoch: value,
  }: { epoch: number }): Promise<number> => {
    const cached = epochs.get(value)
    if (cached !== undefined) return cached
    const epoch = await client.get({
      path: `/epochs/${value}`,
      schema: cardanoEpochSchema,
    })
    if (epoch.epoch !== value) throw new Error('Cardano payout epoch mismatch')
    epochs.set(value, epoch.start_time * 1000)
    return epoch.start_time * 1000
  }
  /**
   * 1件の未解析Txを全登録済みStake集合に対して解析する。
   * @param txHash Tx Hash
   * @returns 既存Action形式
   */
  const analyzeTx = async ({
    txHash,
  }: { txHash: string }): Promise<readonly Action[]> => {
    const transaction = await client.get({
      path: `/txs/${txHash}`,
      schema: cardanoTransactionSchema,
    })
    const utxos = await client.get({
      path: `/txs/${txHash}/utxos`,
      schema: cardanoUtxosSchema,
    })
    const withdrawals =
      transaction.valid_contract && transaction.withdrawal_count > 0
        ? await readList({
            client,
            path: `/txs/${txHash}/withdrawals`,
            schema: cardanoWithdrawalSchema,
          })
        : []
    if (
      transaction.valid_contract &&
      withdrawals.length !== transaction.withdrawal_count
    ) {
      throw new Error('Incomplete Cardano reward withdrawals')
    }
    const depositDeltas = new Map<string, bigint>()
    if (transaction.valid_contract && transaction.stake_cert_count > 0) {
      const certificates = await readList({
        client,
        path: `/txs/${txHash}/stakes`,
        schema: cardanoStakeCertificateSchema,
      })
      if (certificates.length !== transaction.stake_cert_count)
        throw new Error('Incomplete Cardano stake certificates')
      const activeDeposits = new Map<string, bigint>()
      const initializedDeposits = new Set<string>()
      for (const certificate of certificates.sort(
        (left, right) => left.cert_index - right.cert_index,
      )) {
        const stake = certificate.address
        if (!stakeAddresses.has(stake)) continue
        let history = registrations.get(stake)
        if (history === undefined) {
          history = await readList({
            client,
            path: `/accounts/${stake}/registrations`,
            schema: cardanoRegistrationSchema,
          })
          registrations.set(stake, history)
        }
        if (!initializedDeposits.has(stake)) {
          initializedDeposits.add(stake)
          for (const registration of history.filter(
            (row) => row.tx_slot < transaction.slot,
          )) {
            if (registration.action === 'registered') {
              if (registration.deposit === null)
                throw new Error('Missing historical Cardano stake deposit')
              activeDeposits.set(stake, BigInt(registration.deposit))
            } else activeDeposits.delete(stake)
          }
        }
        if (certificate.registration) {
          const registration = history.find(
            (row) => row.tx_hash === txHash && row.action === 'registered',
          )
          if (
            registration?.deposit === undefined ||
            registration.deposit === null
          )
            throw new Error('Missing Cardano stake registration deposit')
          const amount = BigInt(registration.deposit)
          depositDeltas.set(stake, (depositDeltas.get(stake) ?? 0n) + amount)
          activeDeposits.set(stake, amount)
        } else {
          const amount = activeDeposits.get(stake)
          if (amount === undefined)
            throw new Error('Missing Cardano stake deposit refund history')
          depositDeltas.set(stake, (depositDeltas.get(stake) ?? 0n) - amount)
          activeDeposits.delete(stake)
        }
      }
    }
    for (const row of [...utxos.inputs, ...utxos.outputs]) {
      for (const amount of row.amount) {
        if (amount.unit === 'lovelace' || assets.has(amount.unit)) continue
        let metadata: unknown
        try {
          metadata = await client.get({
            path: `/assets/${amount.unit}`,
            schema: cardanoAssetMetadataSchema,
          })
        } catch (error) {
          if (!(error instanceof BlockfrostError) || error.status !== 404)
            throw error
        }
        assets.set(
          amount.unit,
          createCardanoNativeAsset({
            unit: amount.unit,
            metadata,
            currencies: params.cryptoCurrencies,
            existing: params.cryptoCurrencies.find(
              (asset) => asset.id === `cardano:${amount.unit}`,
            ),
          }),
        )
      }
    }
    return analyzeCardanoTransaction({
      transaction,
      utxos,
      stakeAddresses,
      enterpriseAddresses,
      withdrawals,
      depositDeltas,
      assets,
    })
  }
  try {
    if (targets.length === 0) return
    const retryAfter = await readFromDatabase((database) =>
      getSyncTimestamp({ database, key: 'cardano-blockfrost-retry-after' }),
    )
    if (Date.now() < retryAfter) return
    for (const [position, account] of targets.entries()) {
      if (halted) break
      params.onProgress({
        status: 'check-account',
        progress:
          targets.length === 0 ? 100 : (position / targets.length) * 100,
      })
      if (
        account.cardanoIndexComplete &&
        account.lastSyncedAt !== null &&
        Date.now() - account.lastSyncedAt.getTime() < 3_600_000
      )
        continue
      await writeToDatabase((database) =>
        setCardanoIndexComplete({
          database,
          accountId: account.id,
          complete: false,
        }),
      )
      try {
        const stakeAddress = account.cardanoStakeAddress
        // 関連Addressは毎回先頭から再探索し、追加されたAddressを必ずbackfillする。
        if (stakeAddress !== null)
          await client.list({
            path: `/accounts/${account.cardanoStakeAddress}/addresses`,
            schema: cardanoPaymentAddressSchema,
            emptyOn404: true,
            onPage: async ({ items, page, complete }) => {
              await writeToDatabase((database) =>
                saveCardanoSyncPage({
                  database,
                  accountId: account.id,
                  stream: 'addresses',
                  page,
                  complete,
                  paymentAddresses: items
                    .map((item) => item.address)
                    .filter((address) =>
                      isOwnedCardanoPaymentAddress({
                        address,
                        stakeAddress,
                      }),
                    ),
                }),
              )
            },
          })
        // 完全再同期で探索状態が削除されても登録元のEnterprise Addressを復元する。
        else
          await writeToDatabase((database) =>
            saveCardanoSyncPage({
              database,
              accountId: account.id,
              stream: 'addresses',
              page: 1,
              complete: true,
              paymentAddresses: [account.cardanoAddress],
            }),
          )
        const state = await readFromDatabase((database) =>
          getCardanoSyncState({ database, accountId: account.id }),
        )
        for (const address of state.addresses) {
          const stream = `tx:${address}`
          await client.list({
            path: `/addresses/${address}/transactions`,
            schema: cardanoTransactionIndexSchema,
            emptyOn404: true,
            page:
              state.checkpoints.find(
                (checkpoint) => checkpoint.stream === stream,
              )?.page ?? 1,
            onPage: async ({ items, page, complete }) => {
              await writeToDatabase((database) =>
                saveCardanoSyncPage({
                  database,
                  accountId: account.id,
                  stream,
                  page,
                  complete,
                  events: items
                    .filter(
                      (item) => item.block_time * 1000 >= params.startTime,
                    )
                    .map((item) => ({
                      kind: 'tx',
                      txHash: item.tx_hash,
                      timestamp: item.block_time * 1000,
                    })),
                }),
              )
            },
          })
        }
        if (account.cardanoStakeAddress !== null) {
          // 自己Payment Addressに出力されない引出しもStake側の索引から捕捉する。
          await client.list({
            path: `/accounts/${account.cardanoStakeAddress}/withdrawals`,
            schema: z.object({
              tx_hash: z.string().regex(/^[0-9a-f]{64}$/),
              amount: z.string().regex(/^[0-9]+$/),
            }),
            emptyOn404: true,
            page:
              state.checkpoints.find(
                (checkpoint) => checkpoint.stream === 'withdrawals',
              )?.page ?? 1,
            onPage: async ({ items, page, complete }) => {
              const events: CardanoEvent[] = []
              for (const item of items) {
                const tx = await client.get({
                  path: `/txs/${item.tx_hash}`,
                  schema: cardanoTransactionSchema,
                })
                if (tx.block_time * 1000 >= params.startTime)
                  events.push({
                    kind: 'tx',
                    txHash: item.tx_hash,
                    timestamp: tx.block_time * 1000,
                  })
              }
              await writeToDatabase((database) =>
                saveCardanoSyncPage({
                  database,
                  accountId: account.id,
                  stream: 'withdrawals',
                  page,
                  complete,
                  events,
                }),
              )
            },
          })
          await client.list({
            path: `/accounts/${account.cardanoStakeAddress}/registrations`,
            schema: cardanoRegistrationSchema,
            emptyOn404: true,
            page:
              state.checkpoints.find(
                (checkpoint) => checkpoint.stream === 'registrations',
              )?.page ?? 1,
            onPage: async ({ items, page, complete }) => {
              await writeToDatabase((database) =>
                saveCardanoSyncPage({
                  database,
                  accountId: account.id,
                  stream: 'registrations',
                  page,
                  complete,
                  events: items
                    .filter(
                      (item) => item.block_time * 1000 >= params.startTime,
                    )
                    .map((item) => ({
                      kind: 'tx',
                      txHash: item.tx_hash,
                      timestamp: item.block_time * 1000,
                    })),
                }),
              )
            },
          })
          const latest = await client.get({
            path: '/epochs/latest',
            schema: cardanoEpochSchema,
          })
          // 報酬源がページ境界をまたぐEpochは次ページまで保留する。再開は先頭から冪等取得。
          let carry: z.infer<typeof cardanoRewardSchema>[] = []
          const seenRewards = new Map<string, string>()
          await client.list({
            path: `/accounts/${account.cardanoStakeAddress}/rewards`,
            schema: cardanoRewardSchema,
            emptyOn404: true,
            onPage: async ({ items, page, complete }) => {
              const fresh = items.filter((item) => {
                const key = `${item.epoch}:${item.type}:${item.pool_id}`
                const previous = seenRewards.get(key)
                if (previous !== undefined && previous !== item.amount)
                  throw new Error('Cardano reward changed during pagination')
                if (previous !== undefined) return false
                seenRewards.set(key, item.amount)
                return true
              })
              const records = [...carry, ...fresh]
              const lastEpoch = records.at(-1)?.epoch
              carry = complete
                ? []
                : records.filter((record) => record.epoch === lastEpoch)
              const grouped = new Map<number, bigint>()
              for (const record of records) {
                if (!complete && record.epoch === lastEpoch) continue
                // Pool返還は預託資産内の移動であり収益にしない。
                if (record.type === 'pool_deposit_refund') continue
                grouped.set(
                  record.epoch + 2,
                  (grouped.get(record.epoch + 2) ?? 0n) + BigInt(record.amount),
                )
              }
              const events: CardanoEvent[] = []
              for (const [payoutEpoch, amount] of grouped) {
                if (payoutEpoch > latest.epoch || amount === 0n) continue
                const known = state.rewards.find(
                  (reward) => reward.payoutEpoch === payoutEpoch,
                )
                if (known !== undefined) {
                  if (known.rewardAmount !== amount.toString())
                    throw new Error(
                      'Cardano reward total changed; full resynchronization is required',
                    )
                  continue
                }
                const timestamp = await epochTime({ epoch: payoutEpoch })
                if (timestamp >= params.startTime)
                  events.push({
                    kind: 'reward',
                    payoutEpoch,
                    timestamp,
                    rewardAmount: amount.toString(),
                  })
              }
              await writeToDatabase((database) =>
                saveCardanoSyncPage({
                  database,
                  accountId: account.id,
                  stream: 'rewards',
                  page,
                  complete,
                  events,
                }),
              )
            },
          })
        }
        await writeToDatabase((database) =>
          setCardanoIndexComplete({
            database,
            accountId: account.id,
            complete: true,
          }),
        )
      } catch (error) {
        report({ error })
      }
    }
    const pending = new Map<
      string,
      {
        source: ActionSourceCardano
        timestamp: number
        rewardAmount: string | null
      }
    >()
    for (const account of targets) {
      const state = await readFromDatabase((database) =>
        getCardanoSyncState({ database, accountId: account.id }),
      )
      for (const event of state.pending) {
        let source: ActionSourceCardano
        if (event.kind === 'tx' && event.txHash !== null)
          source = {
            id: event.sourceId,
            type: 'cardano',
            cardanoKind: 'tx',
            cardanoTxHash: event.txHash,
          }
        else if (
          event.kind === 'reward' &&
          event.payoutEpoch !== null &&
          account.cardanoStakeAddress !== null
        )
          source = {
            id: event.sourceId,
            type: 'cardano',
            cardanoKind: 'reward',
            cardanoStakeAddress: account.cardanoStakeAddress,
            cardanoPayoutEpoch: event.payoutEpoch,
          }
        else throw new Error('Invalid Cardano event index')
        pending.set(event.sourceId, {
          source,
          timestamp: event.timestamp.getTime(),
          rewardAmount: event.rewardAmount,
        })
      }
    }
    let done = 0
    for (const event of pending.values()) {
      if (halted) break
      params.onProgress({
        status: 'analyze',
        progress: (done / pending.size) * 100,
      })
      try {
        const actions: readonly Action[] =
          event.source.cardanoKind === 'tx'
            ? await analyzeTx({ txHash: event.source.cardanoTxHash })
            : [
                {
                  source: event.source.id,
                  timestamp: event.timestamp,
                  order: 0,
                  type: 'staking-reward',
                  evidence: 'system-rule',
                  transfers: [
                    {
                      direction: 'in',
                      to: event.source.cardanoStakeAddress,
                      asset: cardanoAda,
                      amount: {
                        value: BigInt(
                          cardanoQuantitySchema.parse(event.rewardAmount),
                        ),
                        decimals: 6,
                      },
                    },
                  ],
                },
              ]
        const priced = await setActionPrices({
          actions,
          fiat: params.fiat,
          dataSource: params.priceDataSource,
        })
        await writeToDatabase((database) =>
          saveCardanoActions({
            database,
            source: event.source,
            actions: priced,
          }),
        )
      } catch (error) {
        report({ error })
      }
      done++
    }
  } catch (error) {
    report({ error })
  } finally {
    try {
      if (retryAt > 0)
        await writeToDatabase((database) =>
          upsertSyncTimestamp({
            database,
            key: 'cardano-blockfrost-retry-after',
            timestamp: retryAt,
          }),
        )
    } finally {
      analyzing = false
      params.onProgress(undefined)
    }
  }
}

/**
 * 取引証書など少量のページ一覧を最後まで検証して取得する。
 * @param params API client、一覧パス、要素schema
 * @returns 完全に取得された検証済み一覧
 */
const readList = async <T>(params: {
  client: BlockfrostClient
  path: string
  schema: z.ZodType<T, z.ZodTypeDef, unknown>
}): Promise<T[]> => {
  const rows: T[] = []
  await params.client.list({
    path: params.path,
    schema: params.schema,
    onPage: async ({ items }) => {
      rows.push(...items)
    },
  })
  return rows
}
